import React, { useEffect, useState, useRef, useCallback } from "react";
import { connect } from "react-redux";
import axios from "axios";
import FormField from '../../../../dynamicFormBuilder/FormField';
import data from "../../../../assets/apiRoutes.json";
import { loadLookupFromDataSource, loadLookupFromStaticDataSource } from '../../../../common/Utils';
import { layoutFieldNames } from '../../../../id/common/customer/CustomerInfoForm';
import { CUSTOMER_STATUS_OPTIONS, PRICE_LEVEL_OPTIONS } from '../../../../const/constants';
import {
    FORM_LAYOUTS,
    ID_CODE_TYPE_BILLING_CYCLE,
    PARTIAL_SHIPMENT_OPTIONS,
    CURRENCY_FORMAT,
} from '../constants';
import '../../../../common/css/searchPage.css';

// Additional tab — the four groups of OMD0010's "&Additional" page:
// Miscellaneous, Balance, A/R Control Account and Sales Order Entry. Read-only
// report view; coded fields render as lookup-bound selects so the stored rowid
// resolves to its code, like the Customer Data Entry screen.
const select = (name, label) => ({
    "name": name, "tableName": "IDCUSTOMER", "label": label, "type": "select",
    "required": false, "visible": true, "isLabelChanged": false, "readOnly": true,
});
const text = (name, label, type = "text") => ({
    "name": name, "tableName": "IDCUSTOMER", "label": label, "type": type,
    "required": false, "visible": true, "isLabelChanged": false, "readOnly": true,
});

const AdditionalLayout = [
    {
        "groupId": 1,
        "label": "Miscellaneous",
        "fields": [
            select("CustomerR_TAXCODE", "Tax Code"),
            select("CustomerBILLINGCYCLE", "Billing Cycle"),
            select("CustomerSTATUS", "Status"),
            text("CustomerRESALENUMBER", "Resale #"),
            // Long free text — renders multiline per the grid/form standard.
            text("CustomerCOMMENT", "Comment", "textarea"),
            select("CustomerR_TERMS", "Terms"),
        ]
    },
    {
        "groupId": 2,
        "label": "Balance",
        "fields": [
            { "name": "CurrentBalance", "tableName": "IDCUSTOMER", "label": "Current Bal", "type": "number", "format": CURRENCY_FORMAT, "required": false, "visible": true, "isLabelChanged": false, "readOnly": true },
        ]
    },
    {
        "groupId": 3,
        "label": "A/R Control Account",
        "fields": [
            select("CustomerARACCOUNT", "AR Account"),
            select("CustomerR_GLDEPARTMENT", "Department"),
            select("CustomerR_GLFUND", "Fund"),
            select("CustomerR_CAJOB", "Job"),
        ]
    },
    {
        "groupId": 4,
        "label": "Sales Order Entry",
        "fields": [
            select("CustomerSOINVENTORYPRICELEVEL", "Price Level"),
            select("CustomerR_PRICESHEDULE", "Price Sched"),
            text("CustomerNORMALOPENDAYS", "Open Days", "number"),
            select("CustomerPARTIALSHIPMENT", "Partial Ship"),
            // SOMISCFEEFLAG has no flag-description set defined anywhere in the
            // web app yet, so the raw stored value is shown rather than an
            // invented option list.
            text("CustomerSOMISCFEEFLAG", "Misc Fee"),
            select("CustomerR_OMDEFAULTTRANSPROFILE", "Profile"),
        ]
    }
];

// Derived from AdditionalLayout so a field added to the layout is never
// silently missing from form state - CurrentBalance is excluded since it
// comes from a separate summary-totals fetch below, not props.customer.
const ADDITIONAL_FIELDS = layoutFieldNames(AdditionalLayout).filter((name) => name !== 'CurrentBalance');

const emptyFormFields = () => {
    const acc = { CurrentBalance: 0 };
    ADDITIONAL_FIELDS.forEach((key) => { acc[key] = ''; });
    return acc;
};

const AdditionalTab = (props) => {
    const accessToken = props.oidc.user.access_token;
    const currentUser = props.oidc.user;
    const DYNAMICFORMSETTING = props.oidc?.user?.profile?.DYNAMICFORMSETTING;
    const formRefs = useRef({});
    const [formFields, setformFields] = useState(emptyFormFields());

    // Customer master + control-account references — reuse the customer already
    // loaded by the Primary tab (no second GetCustomerDataByRowId call).
    useEffect(() => {
        const customer = props.customer;
        if (customer && customer.ROWID) {
            setformFields((prev) => {
                const next = { ...prev };
                ADDITIONAL_FIELDS.forEach((key) => { next[key] = customer[key] ?? ''; });
                return next;
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.customer]);

    // Current A/R balance from the summary totals endpoint (not provided by the
    // shared customer payload).
    useEffect(() => {
        if (!props.id) {
            return;
        }
        axios.get(`${window._env_.REACT_APP_APIS}/${data.ID.GetSummaryTotals}?customerId=${props.id}`, {
            headers: { access_token: accessToken, Authorization: `Bearer ${accessToken}` },
        }).then((res) => {
            const totals = res.data;
            if (totals) {
                setformFields((prev) => ({ ...prev, CurrentBalance: totals.currentBalance ?? 0 }));
            }
        }).catch((err) => {
            console.error("Failed to load customer summary totals:", err);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.id]);

    const handleFieldChange = useCallback((name, value) => {
    }, []);

    const handleFormLoad = useCallback(() => {
        const api = (route) => `${window._env_.REACT_APP_APIS}/${route}`;
        loadLookupFromDataSource(accessToken, formFields, formRefs, "CustomerR_TAXCODE", api(data.ID.GetIDTax));
        loadLookupFromDataSource(accessToken, formFields, formRefs, "CustomerR_TERMS", api(data.ID.GetIDTerms));
        // Billing cycle is an ID code list, keyed by its own name.
        loadLookupFromDataSource(accessToken, formFields, formRefs, "CustomerBILLINGCYCLE", api(data.ID.GetIDCodes),
            'name', 'name', { codeType: ID_CODE_TYPE_BILLING_CYCLE });
        loadLookupFromDataSource(accessToken, formFields, formRefs, "CustomerARACCOUNT", api(data.AC.GetAcAccounts));
        loadLookupFromDataSource(accessToken, formFields, formRefs, "CustomerR_GLDEPARTMENT", api(data.AC.GetDepartments));
        loadLookupFromDataSource(accessToken, formFields, formRefs, "CustomerR_GLFUND", api(data.FA.GetFafunds));
        loadLookupFromDataSource(accessToken, formFields, formRefs, "CustomerR_CAJOB", api(data.CA.GetCaJobs));
        loadLookupFromDataSource(accessToken, formFields, formRefs, "CustomerR_PRICESHEDULE", api(data.IM.GetPriceSchedule));
        loadLookupFromDataSource(accessToken, formFields, formRefs, "CustomerR_OMDEFAULTTRANSPROFILE", api(data.OM.GetProfilesLookup));
        loadLookupFromStaticDataSource("CustomerSTATUS", CUSTOMER_STATUS_OPTIONS, formFields, formRefs, "id", "name");
        loadLookupFromStaticDataSource("CustomerSOINVENTORYPRICELEVEL", PRICE_LEVEL_OPTIONS, formFields, formRefs, "id", "name");
        loadLookupFromStaticDataSource("CustomerPARTIALSHIPMENT", PARTIAL_SHIPMENT_OPTIONS, formFields, formRefs, "id", "name");
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <FormField
            currrentUser={currentUser}
            layoutId={FORM_LAYOUTS.Additional.id}
            layoutName={FORM_LAYOUTS.Additional.name}
            defaultFormSchema={AdditionalLayout}
            formFields={formFields}
            setformFields={setformFields}
            handleFieldValueChange={handleFieldChange}
            DYNAMICFORMSETTING={DYNAMICFORMSETTING}
            formRefs={formRefs}
            onFormLoad={handleFormLoad}
            noSpacingBeforeFirstRow={true}
        />
    );
};

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}
export default connect(mapStateToProps)(AdditionalTab);
